import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalEntries: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalEntries,
  pageSize,
  onPageChange,
  loading = false,
}) => {
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const getResultsMessage = () => {
    if (totalEntries === 0) {
      return 'No results found';
    }
    if (totalEntries <= pageSize) {
      return `Showing ${totalEntries} results`;
    }
    const startEntry = (currentPage - 1) * pageSize + 1;
    const endEntry = Math.min(currentPage * pageSize, totalEntries);
    return `Showing ${startEntry} to ${endEntry} of ${totalEntries} results`;
  };

  const renderPageNumbers = () => {
    if (totalPages === 0) return null;

    const pages: (number | string)[] = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i += 1) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      const leftBound = Math.max(2, currentPage - 2);
      const rightBound = Math.min(totalPages - 1, currentPage + 2);

      if (leftBound > 2) {
        pages.push('ellipsis-left');
      }

      for (let i = leftBound; i <= rightBound; i += 1) {
        pages.push(i);
      }

      if (rightBound < totalPages - 1) {
        pages.push('ellipsis-right');
      }

      pages.push(totalPages);
    }

    return pages.map((page) => {
      if (typeof page === 'string') {
        return (
          <span
            key={page}
            style={{
              width: '38px',
              height: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#344054',
            }}
          >
            ...
          </span>
        );
      }

      return (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          disabled={loading}
          className="hover:opacity-70 disabled:cursor-not-allowed"
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '6px',
            backgroundColor: page === currentPage ? '#404040' : '#F6F5F3',
            border: `1px solid ${page === currentPage ? '#404040' : '#D0D5DD'}`,
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 400,
            color: page === currentPage ? '#EAECF0' : '#344054',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {page}
        </button>
      );
    });
  };

  return (
    <div className="flex justify-between items-center mt-6">
      {/* Results count */}
      <div
        style={{
          fontFamily: 'Oxygen',
          fontSize: '14px',
          color: '#667085',
        }}
      >
        {getResultsMessage()}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1 || loading}
          className="flex items-center gap-2 hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            height: '38px',
            borderRadius: '6px',
            backgroundColor: '#F6F5F3',
            border: '1px solid #D0D5DD',
            padding: '0 16px',
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 400,
            color: '#344054',
          }}
        >
          <ArrowLeft size={20} color="#344054" strokeWidth={1.67} aria-hidden="true" />
          Previous
        </button>

        {/* Page numbers */}
        <div className="flex gap-2">{renderPageNumbers()}</div>

        {/* Next button */}
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages || loading}
          className="flex items-center gap-2 hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            height: '38px',
            borderRadius: '6px',
            backgroundColor: '#F6F5F3',
            border: '1px solid #D0D5DD',
            padding: '0 16px',
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 400,
            color: '#344054',
          }}
        >
          Next
          <ArrowRight size={20} color="#344054" strokeWidth={1.67} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
